import { Component } from '@theme/component';
import { firePendingSubmissionIfAny, stashPendingSubmission } from './oc-inquiry-measurement-v2.js';

/** need values that reveal the order number field. */
const ORDER_NUMBER_VISIBLE_FOR = new Set(['new-kit', 'existing-ramp']);

/** sessionStorage/Klaviyo key for this form family. */
const FORM_KEY = 'installation';

/**
 * @typedef {object} InstallationFormRefs
 * @property {HTMLSelectElement} [needInput]
 * @property {HTMLTextAreaElement} [detailsInput]
 * @property {HTMLSelectElement} [stateInput]
 * @property {HTMLInputElement} [cityInput]
 * @property {HTMLInputElement} [firstNameInput]
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLInputElement} [phoneInput]
 * @property {HTMLInputElement} [productInput]
 * @property {HTMLElement} [orderNumberField]
 * @property {HTMLInputElement} [orderNumberInput]
 * @property {HTMLSelectElement} [timingInput]
 * @property {HTMLInputElement} [marketingInput]
 * @property {HTMLTextAreaElement} [bodyInput]
 * @property {HTMLButtonElement} [submitButton]
 *
 * @extends {Component<InstallationFormRefs>}
 */
class InstallationFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Prefill the product field when arriving from a product page, e.g. ?product=Garage+Mini+Ramp.
    const { productInput } = this.refs;
    if (productInput instanceof HTMLInputElement) {
      const product = new URLSearchParams(window.location.search).get('product');
      if (product) productInput.value = product;
    }

    this.#syncNeedBranch();

    // `.quote-form__success` only renders when Liquid's `form.posted_successfully?`
    // is true, so its presence is a reliable success signal after the page reload.
    firePendingSubmissionIfAny(FORM_KEY, Boolean(this.querySelector('.quote-form__success')));
  }

  /** Runs when the "What do you need installed?" select changes. */
  handleNeedChange() {
    this.#syncNeedBranch();
  }

  /**
   * Composes the hidden `contact[body]` field from the individual inputs right
   * before the native form submits, since Shopify's contact endpoint only
   * delivers `name`, `email`, `phone` and `body`. Also stashes the submission
   * for the Klaviyo data push, picked up after the page reloads on success.
   */
  handleSubmit() {
    const { bodyInput, submitButton } = this.refs;

    if (bodyInput) {
      bodyInput.value = this.#composeBody();
    }

    this.#stashKlaviyoSubmission();

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }
  }

  #syncNeedBranch() {
    const { needInput, orderNumberField, orderNumberInput } = this.refs;
    const key = needInput?.selectedOptions[0]?.dataset.key ?? '';
    const showOrderNumber = ORDER_NUMBER_VISIBLE_FOR.has(key);

    if (orderNumberField) orderNumberField.hidden = !showOrderNumber;
    if (orderNumberInput instanceof HTMLInputElement) orderNumberInput.disabled = !showOrderNumber;
  }

  #stashKlaviyoSubmission() {
    const {
      needInput,
      detailsInput,
      stateInput,
      cityInput,
      productInput,
      orderNumberInput,
      timingInput,
      marketingInput,
      firstNameInput,
      emailInput,
      phoneInput,
    } = this.refs;

    stashPendingSubmission(FORM_KEY, {
      email: emailInput?.value,
      firstName: firstNameInput?.value,
      phone: phoneInput?.value,
      sourceLabel: this.dataset.sourceLabel,
      family: 'Installation',
      properties: {
        'What they need installed': needInput?.value,
        'Installation details': detailsInput?.value,
        'Project state': stateInput?.value,
        'Project city': cityInput?.value,
        'Product or ramp name': productInput?.value,
        'Order number':
          orderNumberInput instanceof HTMLInputElement && !orderNumberInput.disabled ? orderNumberInput.value : undefined,
        'Target timing': timingInput?.value,
        'Marketing consent': Boolean(marketingInput?.checked),
      },
    });
  }

  /**
   * @returns {string}
   */
  #composeBody() {
    const { needInput, detailsInput, stateInput, cityInput, productInput, orderNumberInput, timingInput, marketingInput } = this.refs;

    /** @type {[string, string | undefined][]} */
    const rows = [
      ['Source', this.dataset.sourceLabel],
      ['What they need installed', needInput?.value],
      ['Installation details', detailsInput?.value],
      ['Project state', stateInput?.value],
      ['Project city', cityInput?.value],
      ['Product or ramp name', productInput?.value],
    ];

    if (orderNumberInput instanceof HTMLInputElement && !orderNumberInput.disabled && orderNumberInput.value) {
      rows.push(['Order number', orderNumberInput.value]);
    }

    rows.push(
      ['Target timing', timingInput?.value],
      ['Marketing consent', marketingInput?.checked ? 'Yes' : 'No']
    );

    return rows
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}: ${value}`)
      .join('\n');
  }
}

if (!customElements.get('installation-form-component')) {
  customElements.define('installation-form-component', InstallationFormComponent);
}
